import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { notification } from "antd";
import { useTranslation } from "react-i18next";

import { sendOtp, validatedFringerPrint, verifyOtpWithFingerPrint } from "../../Services/AuthenticationServices/AuthenticationServices";
import { setLoginSession } from "../../Services/LocalStorageServices";
import { logError } from "../../Utils/safeLog";
import { translation } from "../../i18n/translationConstant";
import OTPInputComponent from "../Common/Components/OTPModal";
import { getMobileIdentifiers } from "../../Services/IdentifierService/Identifier";

interface FingerprintContextType {
  checkFingerprint: () => Promise<boolean>;
  isFingerprintChecking: boolean;
}

const FingerprintValidationContext = createContext<FingerprintContextType | undefined>(undefined);

export const FingerprintValidationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  const [isOtpVisible, setIsOtpVisible] = useState(false);
  const [otpUniqueID, setOtpUniqueID] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isFingerprintChecking, setIsFingerprintChecking] = useState(false);

  const getMobileIdentifiersFiltered = async () => {
    try {
      const res = await getMobileIdentifiers();
      const primaryIdentifier = res.data.find((identifier: any) => identifier.primary);

      if (primaryIdentifier) {
        const primaryPhone = primaryIdentifier.value;
        setPhoneNumber(primaryPhone);
        return primaryPhone;
      }

      return null;
    } catch (error) {
      logError("Get mobile identifiers error", error);
      return null;
    }
  };

  useEffect(() => {
    getMobileIdentifiersFiltered();
  }, []);

  const checkFingerprint = useCallback(async (): Promise<boolean> => {
    try {
      setIsFingerprintChecking(true);

      console.log("========== FINGERPRINT VALIDATION ==========");

      const result = await validatedFringerPrint();

      if (result.status === "OK") {
        console.log("Fingerprint MATCHED");
        return true;
      }

      if (result.status === "STEP_UP") {
        console.log("Fingerprint mismatch - STEP UP required");

        const phone = await getMobileIdentifiersFiltered();

        if (!phone) {
          console.error("Primary phone number not found");
          return false;
        }

        const response = await sendOtp({ phoneNumber: phone });

        setOtpUniqueID(response.data);
        setIsOtpVisible(true);

        return false;
      }

      return false;
    } catch (error) {
      logError("Fingerprint validation error", error);
      return false;
    } finally {
      setIsFingerprintChecking(false);
    }
  }, []);

  const verifyFingerprintOtp = useCallback(async (otp: string): Promise<boolean> => {
    try {
      if (!otp) {
        notification.error({ message: "Please enter OTP" });
        return false;
      }

      if (!phoneNumber) {
        notification.error({ message: "Primary mobile number not found" });
        return false;
      }

      console.log("Verifying fingerprint OTP");

      const response = await verifyOtpWithFingerPrint({ code: otp, phone_number: phoneNumber, grant_type: "password", otpUniqueID });

      const loginResponse = response.data;

      setLoginSession(loginResponse);
      setIsOtpVisible(false);
      setOtpUniqueID("");

      notification.success({ message: t(translation.lblLoginSuccess) });

      return true;
    } catch (error) {
      logError("Fingerprint OTP verification failed", error);
      return false;
    }
  }, [phoneNumber, otpUniqueID, t]);

  const resendFingerprintOtp = useCallback(async () => {
    try {
      if (!phoneNumber) {
        console.error("Primary phone number not found");
        return;
      }

      const response = await sendOtp({ phoneNumber });

      setOtpUniqueID(response.data);

      console.log("Fingerprint OTP resent");
    } catch (error) {
      logError("OTP resend failed", error);
      notification.error({ message: "Failed to resend OTP" });
    }
  }, [phoneNumber]);

  return (
    <FingerprintValidationContext.Provider value={{ checkFingerprint, isFingerprintChecking }}>
      {children}

      <OTPInputComponent
        isOpen={isOtpVisible}
        onOtpSubmit={verifyFingerprintOtp}
        onCancel={() => {}}
        okText={t(translation.lblConfirm)}
        title={t(translation.lblVerifyIdentity)}
        onResend={resendFingerprintOtp}
        mobileNumber={phoneNumber}
      />
    </FingerprintValidationContext.Provider>
  );
};

export const useFingerprintValidation = () => {
  const context = useContext(FingerprintValidationContext);

  if (!context) {
    throw new Error("useFingerprintValidation must be used inside FingerprintValidationProvider");
  }

  return context;
};